import { ChangeDetectorRef } from '@angular/core';
import html2canvas from 'html2canvas';
import { InsightsGeneratorHelper } from '../insights/insights-generator.helper';

/**
 * CopyClipboardHelper
 *
 * Everything behind the "copy" icons on a chat answer block:
 *  - resolving which insights belong to a block (also used by the export helper),
 *  - copying the Q&A summary text,
 *  - copying the full response (question + markdown data table + insights + summary),
 *  - copying the chart/table as a high-resolution PNG image to the clipboard.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged. No bare `this` existed in this cluster.
 */
export class CopyClipboardHelper {

  static getInsightsForBlock(comp: any, block: any): string[] {
    if (block.messageId === comp.selectedMessageId && comp.selectedMessageId !== null) {
      const insights = Array.isArray(comp.insightsVal) ? comp.insightsVal : (comp.insightsVal ? [comp.insightsVal] : []);
      return insights;
    }

    if (block.messageId && block.mcode) {
      const cached = comp.multiViewInsightsCache[block.messageId]?.[block.mcode] || comp.multiViewInsightsCache['current']?.[block.mcode];
      const isTrend = !!block.isTrendActive;

      if (cached) {
        if (isTrend && cached.trend_insights) return comp.formatInsightsIntoPoints(cached.trend_insights);
        if (!isTrend && cached.insightsVal && Array.isArray(cached.insightsVal) && cached.insightsVal.length > 0) return cached.insightsVal;
        if (!isTrend && cached.standard_insights) return comp.formatInsightsIntoPoints(cached.standard_insights);
        if (!isTrend && cached.rawInsights) return comp.formatInsightsIntoPoints(cached.rawInsights);
        return []; // Module was explicitly toggled, do not fall back to default module insights
      }
    }

    const isTrend = !!block.isTrendActive;
    return isTrend ? (block.insightsTrend || []) : (block.insights || []);
  }

  static copyResponseToClipboard(comp: any, block: any) {
    if (comp.copiedResponseId === block.messageId) return; // Prevent double-clicking

    const responseText = comp.getCurrentQnAResponse(block);
    if (!responseText || responseText === 'Answering question...') return;

    const temp = new DOMParser().parseFromString(responseText, 'text/html').body;
    const textToCopy = temp.textContent || temp.innerText || '';

    navigator.clipboard.writeText(textToCopy).then(() => {
      comp.copiedResponseId = block.messageId;
      setTimeout(() => {
        if (comp.copiedResponseId === block.messageId) {
          comp.copiedResponseId = null;
        }
      }, 2000);
    }).catch((err: any) => {
      console.error('Failed to copy response: ', err);
    });
  }

  static async copyFullResponse(comp: any, block: any) {
    if (comp.copiedResponseId === block.messageId) return;

    try {
      let markdown = '';

      // Extract exact table from the UI block
      const blockId = 'message-' + block.messageId;
      const blockEl = document.getElementById(blockId);
      if (blockEl) {
        const tableDiv = blockEl.querySelector('.table-preview');
        if (tableDiv) {
          const colHeadersElements = tableDiv.querySelectorAll('.preview-columns-header .row-header-title, .preview-columns-header .col-nest-header-txt');
          const headers: string[] = ['Metric'];
          colHeadersElements.forEach((el: any) => {
            const text = el.textContent?.trim();
            if (text) headers.push(text);
          });

          if (headers.length > 1) {
            markdown += `| ${headers.join(' | ')} |\n`;
            markdown += `| ${headers.map(() => '---').join(' | ')} |\n`;

            const rowHeadersElements = tableDiv.querySelectorAll('.table-rows-header-block .row-col-header-title, .table-rows-header-block .row-nest-header-txt');
            const dataContainers = tableDiv.querySelectorAll('.preview-table-body .row-data-container');

            for (let i = 0; i < dataContainers.length; i++) {
              const rowHead = rowHeadersElements[i]?.textContent?.trim() || `Row ${i + 1}`;
              const cells = dataContainers[i].querySelectorAll('.cell-data');
              let rowArray = [rowHead];
              cells.forEach((cell: any) => {
                rowArray.push(cell.textContent?.trim() || '');
              });
              markdown += `| ${rowArray.join(' | ')} |\n`;
            }
          }
        }
      }

      // If no table is visible in DOM, fallback to programmatic data
      if (!markdown) {
        const mcode = block.mcode || comp.mcode;

        if (mcode === 1) {
          const snapData = block.snapshotData || (block.messageId === comp.selectedMessageId ? comp.filteredWidgetData : null);
          if (snapData) {
            markdown = InsightsGeneratorHelper.buildSnapshotMarkdownFromWidget(snapData);
          } else if (block.messageId === comp.selectedMessageId && comp.OutputData && comp.OutputData.length > 0) {
            markdown = InsightsGeneratorHelper.buildSnapshotMarkdownForAPI(comp.OutputData, comp.isTotalBase, comp.selectedWeight, Number(comp.insufficientBase || 0));
          }
        }

        if (!markdown) {
          let opData = null;
          if (block.analyticalData?.member_responses) {
            const viewKey = block.activeView?.key || (mcode === 1 ? 'snapshot' : mcode === 2 ? 'brandanalysis' : 'crosstab');
            opData = block.analyticalData.member_responses[viewKey] || block.analyticalData.member_responses[viewKey.replace('_', '')];
          }
          if (!opData) opData = block.analyticalData || block.parsedData;

          // Fallback for current active block if data not found in block object
          if (!opData && block.messageId === comp.selectedMessageId) {
            if (mcode === 3) markdown = InsightsGeneratorHelper.buildCrosstabMarkdownForAPI(comp.CrossTabOutPutData, comp.isTotalBase, comp.selectedWeight, Number(comp.insufficientBase || 0));
          }

          if (opData && !markdown) {
            const outputResponse = opData.response || opData;
            let dataArr: any[] | null = null;
            const targetData = outputResponse.Data || outputResponse.result?.Data || outputResponse;

            if (targetData) {
              if (typeof targetData === 'string') {
                try { dataArr = JSON.parse(targetData); } catch { }
              } else if (Array.isArray(targetData)) {
                dataArr = targetData;
              }
            }

            if (mcode === 1) {
              if (Array.isArray(dataArr)) {
                markdown = InsightsGeneratorHelper.buildSnapshotMarkdownForAPI(dataArr, comp.isTotalBase, comp.selectedWeight, Number(comp.insufficientBase || 0));
              } else if (opData.metric || (opData.response && opData.response.metric)) {
                markdown = InsightsGeneratorHelper.buildSnapshotMarkdownFromWidget(opData.response || opData);
              }
            } else {
              const crossTabData = dataArr || (opData.response || opData);
              if (Array.isArray(crossTabData)) {
                markdown = InsightsGeneratorHelper.buildCrosstabMarkdownForAPI(crossTabData, comp.isTotalBase, comp.selectedWeight, Number(comp.insufficientBase || 0));
              }
            }
          }
        }
      }

      const notes = [
        "\\* - Indicates Low Base (" + (comp.LowBase || 55) + ")",
        "I/S - Indicates Insufficient Sample (" + (comp.insufficientBase || 25) + ")",
        "NA - Indicates Base = 0"
      ].join('&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;');

      let insightsText = '';
      const insightsArray = comp.getInsightsForBlock(block);
      if (insightsArray && insightsArray.length > 0) {
        insightsText = "### Insights:\n" + insightsArray.map((i: string, index: number) => {
          const trimmed = i.trim();
          return trimmed.startsWith('|') ? (index > 0 ? "\n" + trimmed : trimmed) : "- " + trimmed;
        }).join('\n');
      }

      let qnaText = '';
      const rawQna = comp.getCurrentQnAResponse(block);
      if (rawQna && rawQna !== 'Answering question...') {
        // rawQna is already markdown, avoid HTML conversion
        qnaText = "### Summary:\n" + rawQna.trim();
      }

      const fullText = [
        "### Question: " + (block.question || comp.currentQuestion || ''),
        markdown ? "### Data Table:\n" + markdown : "",
        markdown ? notes : "",
        insightsText,
        qnaText
      ].filter(part => part.trim() !== "").join('\n\n').trim();

      await navigator.clipboard.writeText(fullText);

      comp.copiedResponseId = block.messageId;
      setTimeout(() => {
        if (comp.copiedResponseId === block.messageId) {
          comp.copiedResponseId = null;
        }
      }, 2000);

    } catch (err) {
      console.error('Failed to copy full response: ', err);
    }
  }

  static async handleCopyChartClick(comp: any, event: Event, block: any, popupCdr?: ChangeDetectorRef) {
    event.stopPropagation();
    if (comp.copiedChartId === block.messageId || comp.isCopyingId === block.messageId) return;

    comp.isCopyingId = block.messageId;

    block.isExportspopup = false; // Hide popup so it's not in the screenshot

    // Force a small delay to allow Angular to render the loading state
    setTimeout(async () => {
      try {
        const blockId = 'message-' + block.messageId;
        const blockEl = document.getElementById(blockId);
        if (blockEl) {
          // Capture the chart or table container
          const targetEl = blockEl.querySelector('.table-output-wrapper') ||
            blockEl.querySelector('.table-output-wrapper-snapshot');

          if (targetEl) {
            const hcContainer = targetEl.querySelector('.highcharts-container') as HTMLElement;
            if (hcContainer) {
              let svgString = '';

              // Check if Highcharts object is globally available and has getSVG
              const Highcharts = (window as any).Highcharts;
              const chartIndexAttr = hcContainer.getAttribute('data-highcharts-chart');
              if (Highcharts && Highcharts.charts && chartIndexAttr !== null) {
                const chart = Highcharts.charts[parseInt(chartIndexAttr, 10)];
                if (chart && typeof chart.getSVG === 'function') {
                  svgString = chart.getSVG({
                    sourceWidth: chart.chartWidth,
                    sourceHeight: chart.chartHeight,
                    scale: 3, // Highcharts native SVG export scaling
                    chart: { backgroundColor: '#ffffff' }
                  });
                }
              }

              // If getSVG() isn't available, manually extract and serialize the SVG node
              if (!svgString) {
                const svgEl = hcContainer.querySelector('svg.highcharts-root');
                if (svgEl) {
                  const svgClone = svgEl.cloneNode(true) as SVGElement;
                  if (!svgClone.getAttribute('xmlns')) {
                    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                  }

                  // Scale the SVG natively for extreme clarity
                  const rect = hcContainer.getBoundingClientRect();
                  const scale = 3;
                  svgClone.setAttribute('width', (rect.width * scale) + 'px');
                  svgClone.setAttribute('height', (rect.height * scale) + 'px');
                  if (!svgClone.getAttribute('viewBox')) {
                    svgClone.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);
                  }

                  const serializer = new XMLSerializer();
                  svgString = serializer.serializeToString(svgClone);
                }
              }

              if (svgString) {
                const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(svgBlob);
                const img = new Image();

                img.onload = async () => {
                  const canvas = document.createElement('canvas');
                  const scale = 3; // High resolution / zoom-in
                  canvas.width = hcContainer.offsetWidth * scale;
                  canvas.height = hcContainer.offsetHeight * scale;
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    // Draw crisp SVG background
                    ctx.drawImage(img, 0, 0);

                    // Only overlay html2canvas for snapshot charts to preserve performance
                    const isSnapshot = targetEl.classList.contains('table-output-wrapper-snapshot');
                    const svgNode = hcContainer.querySelector('svg.highcharts-root') as HTMLElement;
                    const originalVisibility = svgNode ? svgNode.style.visibility : '';

                    if (isSnapshot) {
                      // Hide SVG temporarily to capture only the HTML overlays (like useHTML data labels)
                      if (svgNode) svgNode.style.visibility = 'hidden';

                      try {
                        const htmlCanvas = await html2canvas(hcContainer, {
                          useCORS: true,
                          scale: 3,
                          backgroundColor: null,
                          logging: false
                        });
                        // Overlay the HTML labels
                        ctx.drawImage(htmlCanvas, 0, 0);
                      } catch (e) {
                        console.error('Failed to overlay HTML labels', e);
                      }

                      // Restore SVG visibility
                      if (svgNode) svgNode.style.visibility = originalVisibility;
                    }

                    canvas.toBlob(async (blob) => {
                      if (blob) {
                        try {
                          const item = new ClipboardItem({ "image/png": blob });
                          await navigator.clipboard.write([item]);
                          comp.copiedChartId = block.messageId;
                          setTimeout(() => {
                            if (comp.copiedChartId === block.messageId) {
                              comp.copiedChartId = null;
                              comp.cdr.detectChanges();
                              if (popupCdr) popupCdr.detectChanges();
                            }
                          }, 2000);
                        } catch (err) {
                          console.error('Clipboard write failed. Document might not be focused.', err);
                        } finally {
                          comp.isCopyingId = null;
                          comp.cdr.detectChanges();
                          if (popupCdr) popupCdr.detectChanges();
                        }
                      } else {
                        comp.isCopyingId = null;
                        comp.cdr.detectChanges();
                        if (popupCdr) popupCdr.detectChanges();
                      }
                      URL.revokeObjectURL(url);
                    }, 'image/png');
                  }
                };
                img.onerror = (err) => {
                  console.error('SVG Image load error', err);
                  comp.isCopyingId = null;
                  comp.cdr.detectChanges();
                  if (popupCdr) popupCdr.detectChanges();

                  URL.revokeObjectURL(url);
                };
                img.src = url;
                return; // Successfully handled natively
              }
            }

            // 2. Fallback to html2canvas for tables (no SVGs to wash out)
            const canvas = await html2canvas(targetEl as HTMLElement, {
              useCORS: true,
              scale: 3, // Zoom-in for crisp text
              backgroundColor: '#ffffff'
            });

            canvas.toBlob(async (blob) => {
              if (blob) {
                const item = new ClipboardItem({ "image/png": blob });
                await navigator.clipboard.write([item]);

                comp.isCopyingId = null;
                comp.copiedChartId = block.messageId;
                comp.cdr.detectChanges(); // Force UI update
                if (popupCdr) popupCdr.detectChanges();
                setTimeout(() => {
                  if (comp.copiedChartId === block.messageId) {
                    comp.copiedChartId = null;
                    comp.cdr.detectChanges();
                    if (popupCdr) popupCdr.detectChanges();
                  }
                }, 2000);
              }
            }, 'image/png');
          }
        }
      } catch (err) {
        comp.isCopyingId = null;
        comp.cdr.detectChanges();
        if (popupCdr) popupCdr.detectChanges();
        console.error('Failed to copy chart image: ', err);
      }
    }, 150);
  }

  static copyInsightsToClipboard(comp: any, block: any) {
    if (comp.copiedInsightsId === block.messageId) return; // Prevent double-clicking

    const insights = comp.getInsightsForBlock(block);
    if (!insights || insights.length === 0) return;

    // Strip HTML tags from insights using a simple regex since they might be passed through formatMarkdown
    const textToCopy = insights.map((insight: string) => {
      // Remove any HTML tags that might be in the insight
      const temp = new DOMParser().parseFromString(insight, 'text/html').body;
      return temp.textContent || temp.innerText || '';
    }).join('\n\n');

    navigator.clipboard.writeText(textToCopy).then(() => {
      comp.copiedInsightsId = block.messageId;
      setTimeout(() => {
        if (comp.copiedInsightsId === block.messageId) {
          comp.copiedInsightsId = null;
        }
      }, 2000);
    }).catch((err: any) => {
      console.error('Failed to copy insights: ', err);
    });
  }
}
